
import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { TableLoadingOverlay } from '@/components/ui/table-loading-overlay';
import { MultiSelect, Option } from "@/components/ui/multi-select";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import { Search, FileText, Users, BarChart3, Eye, DollarSign, Percent, X, ChevronDown, ChevronRight, Calendar } from 'lucide-react';
import { useTheme } from "@/contexts/ThemeContext";
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from "@/contexts/AuthContext";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { InternalKolTypeAnalytics } from "./InternalKolTypeAnalytics";

interface ContentIdAnalyticsData {
  id: string;
  name: string;
  description: string | null;
  contentId: string;
  tag1: string | null;
  tag2: string | null;
  tag3: string | null;
  totalKols: number;
  totalPosts: number;
  totalViews: number;
  totalExpense: number;
  expensePercentage: number;
  region: string | null;
}

interface FilterState {
  businessUnit: string;
  searchTerm: string;
  selectedTags: string[];
  selectedRegions: string[];
  dateFrom: Date | null;
  dateTo: Date | null;
}

interface GroupedData {
  tag1: string;
  items: ContentIdAnalyticsData[];
  totalKols: number;
  totalPosts: number;
  totalViews: number;
  totalExpense: number;
}

interface CampaignUsageData {
  campaign_id: string;
  usage_count: number;
  total_expense: number;
  total_views: number;
  unique_kols: number;
  earliest_post_date: string | null;
  latest_post_date: string | null;
  business_units: string[] | null;
  first_created: string;
  last_updated: string;
}

export const ContentIdAnalytics = () => {
  const [data, setData] = useState<ContentIdAnalyticsData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState<FilterState>({
    businessUnit: 'all',
    searchTerm: '',
    selectedTags: [],
    selectedRegions: [],
    dateFrom: null,
    dateTo: null
  });
  const [businessUnits, setBusinessUnits] = useState<string[]>([]);
  const [allTags, setAllTags] = useState<string[]>([]);
  const [allRegions, setAllRegions] = useState<string[]>([]);
  const [userName, setUserName] = useState<string | null>(null);
  const [userAccess, setUserAccess] = useState<string | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  
  const { theme } = useTheme();
  const { hasFullAccess, hasBuAccess, userBu } = useAuth();
  const isLightMode = theme === 'light';

  useEffect(() => {
    const access = localStorage.getItem('dashboard_user_access');
    const userName = localStorage.getItem('dashboard_user_name');
    setUserAccess(access);
    setUserName(userName);
  }, []);

  const fetchContentIdAnalytics = async () => {
    try {
      setIsLoading(true);
      
      console.log('Fetching topics data...');
      // Get all topics/campaigns with their tags and description
      const { data: topicsData, error: topicsError } = await supabase
        .from('topics_business_units')
        .select('*')
        .order('name');

      if (topicsError) {
        console.error('Topics error:', topicsError);
        throw topicsError;
      }
      console.log('Topics data:', topicsData);

      console.log('Fetching campaign usage data with date filter...');
      // Use the new database function with date filtering
      const { data: campaignUsageData, error: campaignUsageError } = await supabase
        .rpc('get_campaign_bu_usage_with_date_filter', {
          start_date: filters.dateFrom ? filters.dateFrom.toISOString().split('T')[0] : null,
          end_date: filters.dateTo ? filters.dateTo.toISOString().split('T')[0] : null
        });

      if (campaignUsageError) {
        console.error('Campaign usage error:', campaignUsageError);
        throw campaignUsageError;
      }
      console.log('Campaign usage data length:', campaignUsageData?.length);

      console.log('Fetching KOL DB data...');
      // Get all KOL data for region information
      const { data: kolDbData, error: kolDbError } = await supabase
        .from('kol_db')
        .select('id, region');

      if (kolDbError) {
        console.error('KOL DB error:', kolDbError);
        throw kolDbError;
      }
      console.log('KOL DB data length:', kolDbData?.length);

      // Fetch kol_tracker data to get actual region associations for each campaign
      console.log('Fetching KOL tracker data for region mapping...');
      const { data: kolTrackerData, error: kolTrackerError } = await supabase
        .from('kol_tracker')
        .select('campaign_bu, kol_db_id')
        .not('campaign_bu', 'is', null)
        .not('kol_db_id', 'is', null);

      if (kolTrackerError) {
        console.error('KOL tracker error:', kolTrackerError);
        throw kolTrackerError;
      }
      console.log('KOL tracker data length:', kolTrackerData?.length);

      // Create region mapping and campaign usage mapping
      const kolRegionMap = new Map();
      const uniqueRegions = new Set<string>();
      kolDbData?.forEach(kol => {
        kolRegionMap.set(kol.id, kol.region);
        if (kol.region) {
          uniqueRegions.add(kol.region);
        }
      });

      // Create campaign to regions mapping based on actual KOL associations
      const campaignRegionsMap = new Map<string, Set<string>>();
      kolTrackerData?.forEach(tracker => {
        if (tracker.campaign_bu && Array.isArray(tracker.campaign_bu)) {
          tracker.campaign_bu.forEach(campaign => {
            const campaignStr = String(campaign);
            if (!campaignRegionsMap.has(campaignStr)) {
              campaignRegionsMap.set(campaignStr, new Set());
            }
            const region = kolRegionMap.get(tracker.kol_db_id);
            if (region) {
              campaignRegionsMap.get(campaignStr)?.add(region);
            }
          });
        }
      });

      const campaignUsageMap = new Map<string, CampaignUsageData>();
      campaignUsageData?.forEach(usage => {
        campaignUsageMap.set(usage.campaign_id, usage);
      });

      // Extract unique business units and tags
      const uniqueBusinessUnits = new Set<string>();
      const uniqueTags = new Set<string>();

      // Process analytics data using the database function results
      const analyticsDataWithExpenses = (topicsData || []).map(topic => {
        const searchValue = topic.content_id || topic.id;
        console.log(`Processing topic ${topic.name} with content_id: ${searchValue}`);
        
        // Get campaign usage data from the database function
        const usageData = campaignUsageMap.get(searchValue);
        
        if (usageData) {
          console.log(`Found usage data for ${searchValue}:`, usageData);
        } else {
          console.log(`No usage data found for ${searchValue}`);
        }

        // Extract tags safely as strings
        const tags = Array.isArray(topic.tags) ? topic.tags : [];
        const tag1 = tags.length > 0 && typeof tags[0] === 'string' ? tags[0] : null;
        const tag2 = tags.length > 1 && typeof tags[1] === 'string' ? tags[1] : null;
        const tag3 = tags.length > 2 && typeof tags[2] === 'string' ? tags[2] : null;

        // Add to business units and tags collections
        if (tag1) {
          uniqueBusinessUnits.add(tag1);
        }
        if (tag2) {
          uniqueTags.add(tag2);
        }
        if (tag3) {
          uniqueTags.add(tag3);
        }

        // Get actual regions for this campaign from the KOL associations
        const campaignRegions = campaignRegionsMap.get(searchValue);
        const regionsList = campaignRegions ? Array.from(campaignRegions) : [];

        return {
          id: topic.id,
          name: topic.name,
          description: topic.description,
          contentId: searchValue,
          tag1,
          tag2,
          tag3,
          totalKols: usageData?.unique_kols || 0,
          totalPosts: usageData?.usage_count || 0,
          totalViews: usageData?.total_views || 0,
          totalExpense: usageData?.total_expense || 0,
          expensePercentage: 0, // Will calculate in second pass
          region: regionsList.join(', ') || null
        };
      });

      // Second pass: calculate expense percentages based on Tag 1 groupings
      const tag1ExpenseTotals = new Map<string, number>();
      
      // Calculate total expense for each Tag 1 group
      analyticsDataWithExpenses.forEach(item => {
        if (item.tag1) {
          const currentTotal = tag1ExpenseTotals.get(item.tag1) || 0;
          tag1ExpenseTotals.set(item.tag1, currentTotal + item.totalExpense);
        }
      });

      console.log('Tag1 expense totals:', Object.fromEntries(tag1ExpenseTotals));

      // Calculate percentages based on Tag 1 totals
      const analyticsData = analyticsDataWithExpenses.map(item => {
        let expensePercentage = 0;
        if (item.tag1 && item.totalExpense > 0) {
          const tag1Total = tag1ExpenseTotals.get(item.tag1) || 0;
          if (tag1Total > 0) {
            expensePercentage = (item.totalExpense / tag1Total) * 100;
          }
        }
        
        return {
          ...item,
          expensePercentage
        };
      });

      console.log('Analytics data before BU filtering:', analyticsData.length);

      // Apply BU Access filtering AFTER calculating analytics (app-level filtering)
      let finalAnalyticsData = analyticsData;
      if (hasBuAccess && !hasFullAccess && userBu && Array.isArray(userBu) && userBu.length > 0) {
        // Convert userBu to string array safely
        const userBuStrings = userBu.filter((bu): bu is string => typeof bu === 'string');
        console.log('User BU strings for filtering:', userBuStrings);
        
        finalAnalyticsData = analyticsData.filter(item => {
          // Include items where:
          // 1. Tag1 matches user's BU (content is tagged for their BU)
          // 2. OR there's actual data with matching campaign_bu (they have campaign data)
          const tag1Match = item.tag1 && userBuStrings.includes(item.tag1);
          const hasData = item.totalKols > 0 || item.totalPosts > 0 || item.totalViews > 0 || item.totalExpense > 0;
          
          console.log(`Item ${item.name}: tag1=${item.tag1}, tag1Match=${tag1Match}, hasData=${hasData}`);
          
          return tag1Match || hasData;
        });
      }

      console.log('Final analytics data after BU filtering:', finalAnalyticsData.length);
      setData(finalAnalyticsData);
      setBusinessUnits(Array.from(uniqueBusinessUnits).sort());
      setAllTags(Array.from(uniqueTags).sort());
      setAllRegions(Array.from(uniqueRegions).sort());
      
    } catch (error) {
      console.error('Error fetching content ID analytics:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (userName && userAccess) {
      fetchContentIdAnalytics();
    }
  }, [userName, userAccess, userBu, hasFullAccess, hasBuAccess, filters.dateFrom, filters.dateTo]);

  // Filtered data based on current filters
  const filteredData = useMemo(() => {
    return data.filter(item => {
      const matchesBusinessUnit = filters.businessUnit === 'all' || item.tag1 === filters.businessUnit;
      const matchesSearch = filters.searchTerm === '' || 
        item.contentId.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
        (item.description && item.description.toLowerCase().includes(filters.searchTerm.toLowerCase())) ||
        (item.tag1 && item.tag1.toLowerCase().includes(filters.searchTerm.toLowerCase())) ||
        (item.tag2 && item.tag2.toLowerCase().includes(filters.searchTerm.toLowerCase())) ||
        (item.tag3 && item.tag3.toLowerCase().includes(filters.searchTerm.toLowerCase()));
      
      const matchesTags = filters.selectedTags.length === 0 || 
        filters.selectedTags.some(tag => item.tag2 === tag || item.tag3 === tag);
      
      const matchesRegions = filters.selectedRegions.length === 0 || 
        (item.region && filters.selectedRegions.some(region => 
          item.region?.includes(region)
        ));
      
      // Filter out items with 0 KOLs when date range filters are applied
      const hasDateFilter = filters.dateFrom || filters.dateTo;
      const hasData = item.totalKols > 0 || item.totalPosts > 0 || item.totalViews > 0 || item.totalExpense > 0;
      const shouldIncludeZeroData = !hasDateFilter || hasData;
      
      return matchesBusinessUnit && matchesSearch && matchesTags && matchesRegions && shouldIncludeZeroData;
    });
  }, [data, filters]);

  // Group data by Tag1
  const groupedData = useMemo(() => {
    const groups = new Map<string, GroupedData>();
    
    filteredData.forEach(item => {
      const key = item.tag1 || 'No Business Unit';
      
      if (!groups.has(key)) {
        groups.set(key, {
          tag1: key,
          items: [],
          totalKols: 0,
          totalPosts: 0,
          totalViews: 0,
          totalExpense: 0
        });
      }
      
      const group = groups.get(key)!;
      group.items.push(item);
      group.totalKols += item.totalKols;
      group.totalPosts += item.totalPosts;
      group.totalViews += item.totalViews;
      group.totalExpense += item.totalExpense;
    });
    
    return Array.from(groups.values()).sort((a, b) => a.tag1.localeCompare(b.tag1));
  }, [filteredData]);

  // Create tag options for MultiSelect
  const tagOptions: Option[] = allTags.map(tag => ({
    value: tag,
    label: tag
  }));

  // Create region options for MultiSelect
  const regionOptions: Option[] = allRegions.map(region => ({
    value: region,
    label: region
  }));

  // Calculate summary metrics from filtered data
  const summaryMetrics = useMemo(() => {
    const totalKols = filteredData.reduce((sum, item) => sum + item.totalKols, 0);
    const totalPosts = filteredData.reduce((sum, item) => sum + item.totalPosts, 0);
    const totalViews = filteredData.reduce((sum, item) => sum + item.totalViews, 0);
    const totalExpense = filteredData.reduce((sum, item) => sum + item.totalExpense, 0);
    const totalExpenseAll = data.reduce((sum, item) => sum + item.totalExpense, 0);
    const expensePercentage = totalExpenseAll > 0 ? (totalExpense / totalExpenseAll) * 100 : 0;

    return {
      totalKols,
      totalPosts,
      totalViews,
      totalExpense,
      expensePercentage
    };
  }, [filteredData, data]);

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toLocaleString();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Clear specific filter
  const clearFilter = (filterType: 'businessUnit' | 'tags' | 'regions' | 'dateRange', value?: string) => {
    if (filterType === 'businessUnit') {
      setFilters(prev => ({ ...prev, businessUnit: 'all' }));
    } else if (filterType === 'tags' && value) {
      setFilters(prev => ({ 
        ...prev, 
        selectedTags: prev.selectedTags.filter(tag => tag !== value) 
      }));
    } else if (filterType === 'regions' && value) {
      setFilters(prev => ({ 
        ...prev, 
        selectedRegions: prev.selectedRegions.filter(region => region !== value) 
      }));
    } else if (filterType === 'dateRange') {
      setFilters(prev => ({ ...prev, dateFrom: null, dateTo: null }));
    }
  };

  // Clear all filters
  const clearAllFilters = () => {
    setFilters({
      businessUnit: 'all',
      searchTerm: '',
      selectedTags: [],
      selectedRegions: [],
      dateFrom: null,
      dateTo: null
    });
  };

  // Check if any filters are applied
  const hasActiveFilters = filters.businessUnit !== 'all' || filters.selectedTags.length > 0 || filters.selectedRegions.length > 0 || filters.dateFrom || filters.dateTo;

  // Toggle group expansion
  const toggleGroup = (tag1: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(tag1)) {
      newExpanded.delete(tag1);
    } else {
      newExpanded.add(tag1);
    }
    setExpandedGroups(newExpanded);
  };

  // Format date for display
  const formatDate = (date: Date | null): string => {
    return date ? date.toLocaleDateString() : '';
  };

  if (isLoading) {
    return (
      <div className="border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm relative min-h-[400px]">
        <TableLoadingOverlay isLoading={true} message="Loading content ID analytics..." />
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Enhanced Filters Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 mb-6 border border-gray-200 dark:border-gray-600 shadow-sm">
        {/* Search Bar and Filter Controls - All aligned in one row */}
        <div className="flex flex-wrap items-center gap-4 mb-4">
          {/* Search Bar */}
          <div className="relative flex-1 min-w-[300px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
            <Input
              placeholder="Search content, tags, or campaigns..."
              value={filters.searchTerm}
              onChange={(e) => setFilters(prev => ({ ...prev, searchTerm: e.target.value }))}
              className="pl-10 border-gray-300 dark:border-gray-600 focus:border-blue-500 focus:ring-blue-500"
            />
          </div>

          {/* Business Unit Filter */}
          <div className="w-64">
            <SearchableSelect
              options={[
                { label: "All Business Units", value: "all" },
                ...businessUnits.map(unit => ({ label: unit, value: unit }))
              ]}
              value={filters.businessUnit}
              onChange={(value) => setFilters(prev => ({ ...prev, businessUnit: value }))}
              placeholder="All Business Units"
              badgeVariant="purple"
            />
          </div>
          
          {/* Tag Filter */}
          <div className="w-64">
            <SearchableSelect
              options={tagOptions}
              value={filters.selectedTags[0] || ""}
              onChange={(value) => setFilters(prev => ({ 
                ...prev, 
                selectedTags: value ? [value] : [] 
              }))}
              placeholder="Filter by Tags"
              badgeVariant="blue"
            />
          </div>

          {/* Region Filter */}
          <div className="w-64">
            <SearchableSelect
              options={regionOptions}
              value={filters.selectedRegions[0] || ""}
              onChange={(value) => setFilters(prev => ({ 
                ...prev, 
                selectedRegions: value ? [value] : [] 
              }))}
              placeholder="Filter by Regions"
              badgeVariant="green"
            />
          </div>

          {/* Date Range Filter - Aligned with other filters */}
          <div className="flex items-center gap-2">
            <DateRangePicker
              from={filters.dateFrom}
              to={filters.dateTo}
              onFromChange={(date) => setFilters(prev => ({ ...prev, dateFrom: date }))}
              onToChange={(date) => setFilters(prev => ({ ...prev, dateTo: date }))}
              className="w-auto"
            />
          </div>

          {/* Clear All Filters Button */}
          {hasActiveFilters && (
            <button
              onClick={clearAllFilters}
              className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 underline whitespace-nowrap"
            >
              Clear all filters
            </button>
          )}
        </div>

        {/* Active Filter Badges */}
        {(filters.businessUnit !== 'all' || filters.selectedTags.length > 0 || filters.selectedRegions.length > 0 || filters.dateFrom || filters.dateTo) && (
          <div className="flex flex-wrap gap-2">
            {filters.businessUnit !== 'all' && (
              <Badge 
                variant="outline" 
                className="bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-800"
              >
                Business Unit: {filters.businessUnit}
                <button
                  onClick={() => clearFilter('businessUnit')}
                  className="ml-1 hover:bg-blue-200 dark:hover:bg-blue-800 rounded-full p-1"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )}
            
            {filters.selectedTags.map(tag => (
              <Badge 
                key={tag}
                variant="outline"
                className="bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100 dark:bg-purple-900/20 dark:text-purple-300 dark:border-purple-800"
              >
                Tag: {tag}
                <button
                  onClick={() => clearFilter('tags', tag)}
                  className="ml-1 hover:bg-purple-200 dark:hover:bg-purple-800 rounded-full p-1"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}

            {filters.selectedRegions.map(region => (
              <Badge 
                key={region}
                variant="outline"
                className="bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100 dark:bg-orange-900/20 dark:text-orange-300 dark:border-orange-800"
              >
                Region: {region}
                <button
                  onClick={() => clearFilter('regions', region)}
                  className="ml-1 hover:bg-orange-200 dark:hover:bg-orange-800 rounded-full p-1"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}

            {(filters.dateFrom || filters.dateTo) && (
              <Badge 
                variant="outline"
                className="bg-green-50 text-green-700 border-green-200 hover:bg-green-100 dark:bg-green-900/20 dark:text-green-300 dark:border-green-800"
              >
                <Calendar className="h-3 w-3 mr-1" />
                Date: {formatDate(filters.dateFrom)} - {formatDate(filters.dateTo)}
                <button
                  onClick={() => clearFilter('dateRange')}
                  className="ml-1 hover:bg-green-200 dark:hover:bg-green-800 rounded-full p-1"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )}
          </div>
        )}
      </div>

      {/* Summary Counter Cards */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-8">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0 shadow-lg hover:shadow-xl transition-all duration-300">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-xs font-medium uppercase tracking-wide mb-1">
                  Total KOLs
                </p>
                <p className="text-2xl font-bold">
                  {formatNumber(summaryMetrics.totalKols)}
                </p>
              </div>
              <div className="p-2 bg-white/20 rounded-lg">
                <Users className="h-5 w-5" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white border-0 shadow-lg hover:shadow-xl transition-all duration-300">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-xs font-medium uppercase tracking-wide mb-1">
                  Total Posts
                </p>
                <p className="text-2xl font-bold">
                  {formatNumber(summaryMetrics.totalPosts)}
                </p>
              </div>
              <div className="p-2 bg-white/20 rounded-lg">
                <BarChart3 className="h-5 w-5" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0 shadow-lg hover:shadow-xl transition-all duration-300">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-xs font-medium uppercase tracking-wide mb-1">
                  Total Views
                </p>
                <p className="text-2xl font-bold">
                  {formatNumber(summaryMetrics.totalViews)}
                </p>
              </div>
              <div className="p-2 bg-white/20 rounded-lg">
                <Eye className="h-5 w-5" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white border-0 shadow-lg hover:shadow-xl transition-all duration-300">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-100 text-xs font-medium uppercase tracking-wide mb-1">
                  Total Expense
                </p>
                <p className="text-2xl font-bold">
                  {formatCurrency(summaryMetrics.totalExpense)}
                </p>
              </div>
              <div className="p-2 bg-white/20 rounded-lg">
                <DollarSign className="h-5 w-5" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white border-0 shadow-lg hover:shadow-xl transition-all duration-300">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100 text-xs font-medium uppercase tracking-wide mb-1">
                  Expense %
                </p>
                <p className="text-2xl font-bold">
                  {summaryMetrics.expensePercentage.toFixed(2)}%
                </p>
              </div>
              <div className="p-2 bg-white/20 rounded-lg">
                <Percent className="h-5 w-5" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white border-0 shadow-lg hover:shadow-xl transition-all duration-300">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-indigo-100 text-xs font-medium uppercase tracking-wide mb-1">
                  Content Items
                </p>
                <p className="text-2xl font-bold">
                  {filteredData.length}
                </p>
              </div>
              <div className="p-2 bg-white/20 rounded-lg">
                <FileText className="h-5 w-5" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Grouped Table View */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 shadow-sm">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className={`${isLightMode ? 'bg-gray-100/80 border-b border-gray-300' : 'bg-[#1a1a1a]/80 border-b border-[#4A4A4A]'}`}>
                <TableHead className="w-[50px]"></TableHead>
                <TableHead className="text-base font-semibold">Content ID</TableHead>
                <TableHead className="text-base font-semibold">Description</TableHead>
                <TableHead className="text-base font-semibold">Tag 2</TableHead>
                <TableHead className="text-base font-semibold">Tag 3</TableHead>
                <TableHead className="text-center text-base font-semibold">Total KOLs</TableHead>
                <TableHead className="text-center text-base font-semibold">Total Posts</TableHead>
                <TableHead className="text-center text-base font-semibold">Total Views</TableHead>
                <TableHead className="text-center text-base font-semibold">Total Expense</TableHead>
                <TableHead className="text-center text-base font-semibold">Expense %</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {groupedData.map((group) => (
                <React.Fragment key={group.tag1}>
                  {/* Group Header Row */}
                  <TableRow 
                    className={`${isLightMode ? 'bg-blue-50 hover:bg-blue-100 border-b border-blue-200' : 'bg-blue-900/20 hover:bg-blue-900/30 border-b border-blue-800'} cursor-pointer`}
                    onClick={() => toggleGroup(group.tag1)}
                  >
                    <TableCell className="py-3">
                      {expandedGroups.has(group.tag1) ? (
                        <ChevronDown className="h-5 w-5 text-blue-600" />
                      ) : (
                        <ChevronRight className="h-5 w-5 text-blue-600" />
                      )}
                    </TableCell>
                    <TableCell colSpan={4} className="py-3">
                      <div className="flex items-center gap-3">
                        <Badge className="bg-gradient-to-r from-blue-500 to-blue-600 text-white border-0 text-base px-3 py-1">
                          {group.tag1}
                        </Badge>
                        <span className="text-base font-medium text-gray-600 dark:text-gray-400">
                          ({group.items.length} items)
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center py-3">
                      <div className="text-base font-semibold text-blue-600 dark:text-blue-400">
                        {formatNumber(group.totalKols)}
                      </div>
                    </TableCell>
                    <TableCell className="text-center py-3">
                      <div className="text-base font-semibold text-green-600 dark:text-green-400">
                        {formatNumber(group.totalPosts)}
                      </div>
                    </TableCell>
                    <TableCell className="text-center py-3">
                      <div className="text-base font-semibold text-purple-600 dark:text-purple-400">
                        {formatNumber(group.totalViews)}
                      </div>
                    </TableCell>
                    <TableCell className="text-center py-3">
                      <div className="text-base font-semibold text-emerald-600 dark:text-emerald-400">
                        {formatCurrency(group.totalExpense)}
                      </div>
                    </TableCell>
                    <TableCell className="text-center py-3">
                      <div className="text-base font-semibold text-orange-600 dark:text-orange-400">
                        100%
                      </div>
                    </TableCell>
                  </TableRow>
                  
                  {/* Group Items - Collapsible */}
                  {expandedGroups.has(group.tag1) && group.items.map((item) => (
                    <TableRow 
                      key={item.id}
                      className={`${isLightMode ? 'border-b border-gray-200 hover:bg-gray-50' : 'border-b border-[#4A4A4A] hover:bg-[#ffffff]/[0.04]'}`}
                    >
                      <TableCell className="py-3"></TableCell>
                      <TableCell className="py-3">
                        <div className="text-base font-medium text-blue-600 dark:text-blue-400">
                          {item.contentId}
                        </div>
                      </TableCell>
                      <TableCell className="py-3">
                        <div className="text-base text-gray-600 dark:text-gray-400 max-w-xs truncate">
                          {item.description || '-'}
                        </div>
                      </TableCell>
                      <TableCell className="py-3">
                        {item.tag2 ? (
                          <Badge className="bg-gradient-to-r from-purple-500 to-purple-600 text-white border-0 text-sm">
                            {item.tag2}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="py-3">
                        {item.tag3 ? (
                          <Badge className="bg-gradient-to-r from-orange-500 to-orange-600 text-white border-0 text-sm">
                            {item.tag3}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center py-3">
                        <div className="text-base font-medium text-blue-600 dark:text-blue-400">
                          {formatNumber(item.totalKols)}
                        </div>
                      </TableCell>
                      <TableCell className="text-center py-3">
                        <div className="text-base font-medium text-green-600 dark:text-green-400">
                          {formatNumber(item.totalPosts)}
                        </div>
                      </TableCell>
                      <TableCell className="text-center py-3">
                        <div className="text-base font-medium text-purple-600 dark:text-purple-400">
                          {formatNumber(item.totalViews)}
                        </div>
                      </TableCell>
                      <TableCell className="text-center py-3">
                        <div className="text-base font-medium text-emerald-600 dark:text-emerald-400">
                          {formatCurrency(item.totalExpense)}
                        </div>
                      </TableCell>
                      <TableCell className="text-center py-3">
                        <div className="text-base font-medium text-orange-600 dark:text-orange-400">
                          {item.expensePercentage.toFixed(2)}%
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {filteredData.length === 0 && (
        <div className="flex flex-col items-center justify-center p-12 text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 mt-4">
          <FileText className="h-16 w-16 mb-4 opacity-50" />
          <p className="text-lg font-medium mb-2">No content data found</p>
          <p className="text-sm text-center max-w-md">
            {filters.searchTerm || filters.businessUnit !== 'all' || filters.selectedTags.length > 0 || filters.dateFrom || filters.dateTo ? 
              'No content matches your current filters. Try adjusting your search criteria.' : 
              (hasBuAccess && !hasFullAccess ? 'No content analytics data is available for your business unit.' : 'No content analytics data is available.')
            }
          </p>
        </div>
      )}

      {/* Internal KOL Type Analytics */}
      <InternalKolTypeAnalytics 
        dateFrom={filters.dateFrom} 
        dateTo={filters.dateTo} 
      />
    </div>
  );
};
